import type {
  LoomAirfoil,
  LoomAuthoringDocument,
  LoomFuselageStation,
  LoomSceneNode,
  LoomWingPanel,
} from "./loomAuthoringModel";
import { canonicalLoomDefinitionJson } from "./loomAcceptedDefinitionBinding";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
import { deriveLoomPresetV3Definition } from "./loomPresetV3Binding";

export type LoomRebuildNaca4 = Extract<LoomAirfoil, { family: "naca4" }>;
export type LoomRebuildFuselageStation = LoomFuselageStation;

export interface LoomWingBodyDefinition {
  schema: "loom.wing-body-definition/v1";
  wing: {
    sourceNodeId: string;
    instanceNodeId: string;
    transformNodeIds: string[];
    tip: "flat";
    tipRadiusM: number;
    panel: Omit<LoomWingPanel, "airfoilRoot" | "airfoilTip" | "sectionRecipes"> & {
      airfoilRoot: LoomRebuildNaca4;
      airfoilTip: LoomRebuildNaca4;
    };
    placement: { kind: "translation"; offsetM: [number, number, number] };
  };
  fuselage: {
    sourceNodeId: string;
    stations: LoomRebuildFuselageStation[];
  };
}

type WingDefinition = Extract<Extract<LoomSceneNode, { type: "primitive" }>["definition"], { primitive: "wing" }>;
type FuselageDefinition = Extract<Extract<LoomSceneNode, { type: "primitive" }>["definition"], { primitive: "fuselage" }>;
type PrimitiveNode = Extract<LoomSceneNode, { type: "primitive" }>;
type TranslationPath = {
  instanceNodeId: string;
  offsetM: [number, number, number];
  source: PrimitiveNode;
  transformNodeIds: string[];
  visited: Set<string>;
};

function nearly(left: number, right: number): boolean {
  return Math.abs(left - right) <= 1e-8 * Math.max(1, Math.abs(left), Math.abs(right));
}

function translationPath(
  nodes: ReadonlyMap<string, LoomSceneNode>,
  inputNodeId: string,
): TranslationPath | null {
  const offsetM: [number, number, number] = [0, 0, 0];
  const transformNodeIds: string[] = [];
  const visited = new Set<string>();
  let node = nodes.get(inputNodeId);
  while (node?.type === "transform" && node.operation.transform === "translate") {
    if (visited.has(node.id)) return null;
    visited.add(node.id);
    transformNodeIds.push(node.id);
    offsetM[0] += node.operation.offsetM[0];
    offsetM[1] += node.operation.offsetM[1];
    offsetM[2] += node.operation.offsetM[2];
    node = nodes.get(node.inputNodeId);
  }
  if (node?.type !== "primitive") return null;
  visited.add(node.id);
  return { instanceNodeId: inputNodeId, offsetM, source: node, transformNodeIds, visited };
}

function supportedPanel(wing: WingDefinition): LoomWingPanel | null {
  const panel = wing.panels[0];
  if (wing.panels.length !== 1 || wing.tip !== "flat" || wing.tipRadiusM !== 0
      || !panel || panel.sectionRecipes !== undefined
      || panel.sectionSamples < 11 || panel.sectionSamples > 101
      || panel.sectionSamples % 2 === 0
      || panel.yTipM <= panel.yRootM || panel.chordRootM <= 0 || panel.chordTipM <= 0
      || Math.abs(panel.sweepDeg) >= 89 || Math.abs(panel.dihedralDeg) >= 89
      || panel.airfoilRoot.family !== "naca4" || panel.airfoilTip.family !== "naca4") return null;
  return panel;
}

function supportedStations(fuselage: FuselageDefinition): boolean {
  const stations = fuselage.stations;
  return stations.length >= 3 && stations.length <= 257
    && stations[0]?.kind === "point" && stations[stations.length - 1]?.kind === "point"
    && stations.every((station, index) => index === 0 || station.xM > stations[index - 1]!.xM);
}

export function deriveLoomWingBodyDefinition(
  document: LoomAuthoringDocument,
): LoomWingBodyDefinition | null {
  if (document.rootNodeIds.length !== 1) return null;
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  const root = nodes.get(document.rootNodeIds[0]!);
  if (root?.type !== "boolean" || root.operation !== "union" || root.blendRadiusM !== undefined) return null;
  const visited = new Set<string>([root.id]);
  const paths: TranslationPath[] = [];
  const mirrors: TranslationPath[] = [];
  for (const inputNodeId of root.inputNodeIds) {
    const input = nodes.get(inputNodeId);
    const isMirror = input?.type === "transform" && (
      input.operation.transform === "mirror-y"
      || (input.operation.transform === "mirror" && input.operation.axis === "y"
        && nearly(input.operation.planeOffsetM, 0))
    );
    if (isMirror && input?.type === "transform") {
      visited.add(input.id);
      const path = translationPath(nodes, input.inputNodeId);
      if (!path) return null;
      path.visited.forEach((id) => visited.add(id));
      mirrors.push(path);
    } else {
      const path = translationPath(nodes, inputNodeId);
      if (!path) return null;
      path.visited.forEach((id) => visited.add(id));
      paths.push(path);
    }
  }
  const wings = paths.filter(({ source }) => source.definition.primitive === "wing");
  const fuselages = paths.filter(({ source }) => source.definition.primitive === "fuselage");
  if (wings.length !== 1 || fuselages.length !== 1 || paths.length !== 2 || mirrors.length > 1
      || fuselages[0]!.transformNodeIds.length !== 0
      || mirrors.some(({ source, instanceNodeId }) => (
        source.id !== wings[0]!.source.id || instanceNodeId !== wings[0]!.instanceNodeId
      )) || visited.size !== document.nodes.length) return null;
  const wingPath = wings[0]!;
  const fuselagePath = fuselages[0]!;
  const wing = wingPath.source.definition as WingDefinition;
  const fuselage = fuselagePath.source.definition as FuselageDefinition;
  const panel = supportedPanel(wing);
  if (!panel || !supportedStations(fuselage)) return null;
  const { sectionRecipes: _sectionRecipes, ...normalizedPanel } = panel;
  return {
    schema: "loom.wing-body-definition/v1",
    wing: {
      sourceNodeId: wingPath.source.id,
      instanceNodeId: wingPath.instanceNodeId,
      transformNodeIds: wingPath.transformNodeIds,
      tip: "flat",
      tipRadiusM: wing.tipRadiusM,
      panel: normalizedPanel as LoomWingBodyDefinition["wing"]["panel"],
      placement: { kind: "translation", offsetM: wingPath.offsetM },
    },
    fuselage: {
      sourceNodeId: fuselagePath.source.id,
      stations: fuselage.stations.map((station) => ({ ...station })),
    },
  };
}

export function loomAssetDefinitionMatchesDocument(
  asset: LoomNativeHybridAsset,
  document: LoomAuthoringDocument,
): boolean | null {
  if (document.nodes.some((node) => node.type === "primitive"
      && node.definition.primitive === "wing" && node.definition.junctionFairing !== undefined)) return false;
  const schema = asset.header.acceptedGeometry.schema;
  if (schema === "loom.accepted-mesh/v1") return null;
  const binding = asset.header.acceptedGeometry.definitionBinding;
  if (schema === "loom.accepted-mesh/v3") {
    const decodedHeader = asset.acceptedGeometry.header;
    const union = asset.acceptedGeometry.unionReceipt?.header;
    const declaredUnion = asset.header.acceptedGeometry.unionReceipt;
    if (asset.integrity.status !== "verified"
        || decodedHeader.schema !== schema
        || canonicalLoomDefinitionJson(decodedHeader.definitionBinding)
          !== canonicalLoomDefinitionJson(binding)
        || !union || !declaredUnion
        || union.algorithmId !== "loom.implicit-guided-explicit-union/v1"
        || union.evaluatorId !== "loom.native-wing-body/v3"
        || union.topologyId !== "selected-wing-fuselage-pairwise-union"
        || union.definitionRevision !== binding?.definitionRevision
        || canonicalLoomDefinitionJson(union)
          !== canonicalLoomDefinitionJson(declaredUnion)) return false;
  }
  let definition: Record<string, unknown> | null;
  try {
    definition = schema === "loom.accepted-mesh/v3"
      ? deriveLoomPresetV3Definition(document) as unknown as Record<string, unknown> | null
      : deriveLoomWingBodyDefinition(document) as unknown as Record<string, unknown> | null;
  } catch {
    return false;
  }
  const evaluatorId = schema === "loom.accepted-mesh/v3"
    ? "loom.native-wing-body/v3" : "loom.native-wing-body/v2";
  if (!binding || !definition || binding.evaluatorId !== evaluatorId
      || asset.header.parameters.definitionRevision !== binding.definitionRevision
      || canonicalLoomDefinitionJson(asset.header.parameters.authoritativeDefinition)
        !== canonicalLoomDefinitionJson(binding.definition)) return false;
  return canonicalLoomDefinitionJson(binding.definition) === canonicalLoomDefinitionJson(definition);
}
